/**
 * FuelLoadListCard — single-row anchor card for a Fuel Load
 * transaction inside the Order History list.
 *
 * Fuel loads live in the `Transaction` ledger (not `MenuOrder`), so
 * they don't carry order items / merchant / handoff data. Renders a
 * compact anchor row showing the credited Fuel amount + USD price +
 * "Receipt" ghost action. No expand — the receipt is the row.
 *
 * Status semantics use the design-system colors (amber/green/gray —
 * red is brand/CTA only).
 *
 * Consumed by: pages/menu-orders/MenuOrderListPage.tsx
 */

import { ReactElement } from "react";
import { Transaction } from "@devour/client";
import moment from "moment-timezone";
import classNames from "classnames";
import { FaFire, FaChevronDown } from "react-icons/fa";
import { formatFuelUsd, formatFuelQty } from "@/hooks/useGetFuelBalance";

interface Props {
    transaction: Transaction;
    /**
     * Current Fuel→USD rate (USD per 1 Fuel) from the canonical balance hook —
     * the same `fuelPricePerUsd` used to value the balance everywhere. The exact
     * dollars PAID for a load isn't stored on the Transaction row (it lives on
     * FuelPurchase), so — like the platform's fuel-history endpoint — we value the
     * credited Fuel at the current rate. Undefined until the rate resolves.
     */
    fuelPricePerUsd?: number;
}

function FuelLoadListCard(props: Props): ReactElement {
    if (!props.transaction) return null;

    const fuelAmount = props.transaction.amount ?? 0;
    // USD = Fuel × current rate (balance × fuelPricePerUsd is how USD is derived everywhere). No fixed peg.
    const usdLabel = formatFuelUsd(fuelAmount * (props.fuelPricePerUsd ?? 0), { cents: true });

    const fuelLabel = `+${formatFuelQty(fuelAmount)} FUEL`;

    const dateLine = moment(props.transaction.createdAt)
        .format("MMM DD YYYY · hh:mm A")
        .toUpperCase();

    return (
        <article className={classNames("dp-order")}>
            <div className="dp-order__row">
                <div className="dp-order__icon is-fuel" aria-hidden="true">
                    <FaFire />
                </div>

                <div className="dp-order__main">
                    <span className="dp-order__brand">Fuel Load</span>
                    <span className="dp-order__meta">
                        <span className="dp-status-pill is-complete">Completed</span>
                        <span className="dot">·</span>
                        <strong>{fuelLabel}</strong>
                        <span className="dot">·</span>
                        <span>{dateLine}</span>
                    </span>
                </div>

                <div className="dp-order__total">
                    <span className="dp-order__total-lbl">Total</span>
                    {usdLabel}
                </div>

                <span className="dp-order__action-slot" />

                <FaChevronDown className="dp-order__chevron" style={{ visibility: "hidden" }} />
            </div>
        </article>
    );
}

export default FuelLoadListCard;
